import type { RGBColor } from "react-color";
import type { StyleLibrary } from "schemas/generated/project";

import React, { useState } from "react";

import { PickerComponent } from "@common/designSystem/color/CustomPicker";
import Input from "@common/designSystem/Input";
import { useOverridableInput } from "@editor/components/common/designSystem/hooks/useOverridableInput";
import { useDesignLibraryColors } from "@editor/components/shops-ui/designLibrary/useDesignLibraryColors";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { useDesignLibrary } from "@editor/hooks/useDesignLibrary";
import {
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  getHexColor,
  getInitialColorValue,
  getRGB,
} from "@editor/utils/colors";

import Popover from "@replo/design-system/components/popover/Popover";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import { X } from "lucide-react";
import useMeasure from "react-use-measure";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

interface ColorModifierProps {
  value?: string | null;
  onChange: (color: string | null) => void;
}

interface ColorGridProps {
  styleLibrary: StyleLibrary | null;
  onColorSelect: (color: string) => void;
}

const ColorGrid: React.FC<ColorGridProps> = ({
  styleLibrary,
  onColorSelect,
}) => {
  const { allColors } = useDesignLibraryColors(styleLibrary, {
    includeEmpty: false,
  });

  if (allColors.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center text-center text-muted text-sm w-full">
        <div className="mb-1">No design library colors available</div>
        <div className="text-xs">
          Set up colors in your design library to see them here
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-2 p-1 w-full overflow-y-auto">
      {allColors.map((color) => (
        <button
          key={color.key}
          className="flex flex-col items-center p-1.5 rounded-lg border-[0.5px] border-black/10 hover:bg-surface transition-colors w-full"
          onClick={() => onColorSelect(color.value)}
          title={`${color.name} - ${getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}`}
        >
          <div
            className="w-full aspect-square rounded-lg border-[0.5px] border-black/10 mb-2 max-w-[50px]"
            style={{ backgroundColor: color.value }}
          />
          <div className="flex flex-col items-center text-xs">
            <div className="text-muted font-medium mb-1">{color.name}</div>
            <div className="font-mono text-[10px] text-muted uppercase">
              {getFormattedColorWithoutOpacity(color.value)?.toUpperCase()}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};

interface ColorTriggerButtonProps {
  currentColor: string | null;
  disabled?: boolean;
  size?: "sm" | "md";
  isOpen?: boolean;
}

const ColorTriggerButton: React.FC<
  ColorTriggerButtonProps & {
    onClick: () => void;
    onColorChange: (color: string | null) => void;
  }
> = React.memo(
  ({
    currentColor,
    disabled = false,
    onClick,
    onColorChange,
    isOpen = false,
  }) => {
    const colorSwatch = (
      <div
        className="w-4 h-4 rounded border border-border flex-shrink-0"
        style={{ backgroundColor: currentColor ?? undefined }}
      />
    );

    // Show 6-digit hex in input, but maintain 8-digit hex for internal use
    const displayValue = currentColor
      ? getHexColor(currentColor).toUpperCase()
      : "";

    const inputProps = useOverridableInput({
      value: displayValue,
      /*
       * NOTE (Kurt, 2025-08-26): Preserve the current alpha value when changing colors
       * Extract alpha from current color and apply it to the new color value
       * This ensures alpha is maintained when switching between colors
       */
      onValueChange: (newValue: string) => {
        const currentRgbColor = getRGB(currentColor);
        const newRgbColor = getRGB(newValue);
        newRgbColor.a = currentRgbColor.a ?? 1;
        const hex8Value = getHex8Color(newRgbColor);
        onColorChange(hex8Value);
      },
    });

    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleClick = () => {
      onClick();
      setTimeout(() => {
        inputRef.current?.focus();
        if (!isOpen) {
          inputRef.current?.select();
        }
      }, 0);
    };

    return (
      <div onClick={handleClick} className="w-full">
        <Input
          ref={inputRef}
          {...inputProps}
          size="sm"
          isDisabled={disabled}
          startEnhancer={colorSwatch}
          placeholder="#000000"
          endEnhancer={() => (
            <X
              size={12}
              className="text-muted hover:text-default transition-colors cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onColorChange(null);
              }}
            />
          )}
          layoutClassName="w-full"
        />
      </div>
    );
  },
);

ColorTriggerButton.displayName = "ColorTriggerButton";

const ColorSelector: React.FC<ColorModifierProps> = ({ value, onChange }) => {
  const initialColor = value;
  const projectId = useCurrentProjectId();
  const { currentStyleLibrary } = useDesignLibrary(projectId);

  const [currentValue, setCurrentValue] = useOverridableState(
    initialColor ?? null,
    onChange,
  );

  const debouncedSetCurrentValue = useDebouncedCallback(
    (color: string | null) => {
      setCurrentValue(color);
    },
    500,
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>("styles");
  // NOTE (Kurt, 2025-01-27): Only sync external changes when the popover is closed
  // to prevent feedback loops that would cause the picker to "jump" during user interaction.
  // When the user drags the picker, it updates the selected element, which would normally
  // trigger this effect and reset the picker position mid-drag without this guard.
  // Only sync external changes when the popover is closed
  const [triggerRef, { width: triggerWidth }] = useMeasure();
  const triggerElementRef = React.useRef<HTMLDivElement | null>(null);

  // NOTE (Kurt, 2025-08-24): We want the popover to be at least 250px wide to avoid
  // the popover from being too narrow when the user is selecting a color from the styles tab.
  const effectiveTriggerWidth = Math.max(triggerWidth, 250);

  const [pickerColor, setPickerColor] = useState<RGBColor>(() =>
    getInitialColorValue(currentValue),
  );

  const handleColorPickerChange = React.useCallback(
    (rgb: RGBColor) => {
      setPickerColor(rgb);
      const hexColor = getFormattedColor(getHexColor(rgb));
      // Note (Noah, 2025-09-11): Debounce this callback so that we don't get
      // a new color on every drag event from the picker, just once every 500ms
      if (hexColor) {
        debouncedSetCurrentValue(hexColor);
      }
    },
    [debouncedSetCurrentValue],
  );

  const handleDesignSystemStyleColorSelect = React.useCallback(
    (color: string) => {
      // Note (Noah, 2025-09-11): Do not debounce here, we want to save the color immediately
      setCurrentValue(color);
      setIsOpen(false);

      const newPickerColor = getRGB(color);
      if (newPickerColor) {
        setPickerColor(newPickerColor);
      }
    },
    [setCurrentValue],
  );

  const shouldAllowClose = React.useRef(true);

  /**
   * Handles outside clicks to prevent popover from closing when clicking the trigger.
   *
   * Works with onOpenChange to solve race condition: when clicking trigger while open,
   * Radix still calls onOpenChange(false) after this handler. We set shouldAllowClose
   * flag here so onOpenChange can block the unwanted close.
   */
  const handleInteractOutside = React.useCallback((event: CustomEvent) => {
    const target = event.target as Element;
    if (triggerElementRef.current?.contains(target)) {
      shouldAllowClose.current = false;
      return;
    }
    shouldAllowClose.current = true;
    setIsOpen(false);
  }, []);

  /**
   * Intercepts popover close attempts to prevent closes when clicking trigger.
   *
   * When handleInteractOutside detects a trigger click, it sets shouldAllowClose to false.
   * This handler checks that flag and blocks the close if it was triggered by a trigger click,
   * allowing the popover to stay open when user clicks on the input or color swatch.
   */
  const handleOpenChange = React.useCallback((newIsOpen: boolean) => {
    if (!newIsOpen && !shouldAllowClose.current) {
      shouldAllowClose.current = true;
      return;
    }
    setIsOpen(newIsOpen);
  }, []);

  return (
    <div
      ref={(element) => {
        triggerRef(element);
        // Cast to mutable ref to allow assignment to current property, this is because
        // useMeasure returns a readonly ref
        (
          triggerElementRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = element;
      }}
      className="w-full"
    >
      <Popover
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        side="top"
        sideOffset={8}
        variant="unstyled"
        shouldPreventDefaultOnInteractOutside={true}
        onInteractOutside={handleInteractOutside}
        triggerAsChild
        content={
          <div
            className="flex flex-col items-start gap-3 bg-white border-[0.5px] border-[#CBD5E1] rounded-lg shadow-[4px_4px_16px_rgba(0,0,0,0.06)]"
            style={{ width: effectiveTriggerWidth }}
          >
            <div className="w-full p-3 pb-0">
              <ToggleGroup
                size="sm"
                selectedValue={selectedTab}
                onChange={setSelectedTab}
                options={[
                  { value: "styles", label: "Styles" },
                  { value: "custom", label: "Custom" },
                ]}
                layoutClassName="w-full"
              />
            </div>

            {selectedTab === "styles" ? (
              <div className="px-2 pb-2 w-full">
                <ColorGrid
                  styleLibrary={currentStyleLibrary}
                  onColorSelect={handleDesignSystemStyleColorSelect}
                />
              </div>
            ) : (
              <div className="w-full px-2 pb-2">
                <PickerComponent
                  color={pickerColor}
                  onChange={({ rgb }) => handleColorPickerChange(rgb)}
                />
              </div>
            )}
          </div>
        }
      >
        <div className="w-full">
          <ColorTriggerButton
            currentColor={currentValue}
            onClick={() => setIsOpen(true)}
            onColorChange={(newColor) => setCurrentValue(newColor)}
            isOpen={isOpen}
          />
        </div>
      </Popover>
    </div>
  );
};

export default ColorSelector;
